import { useEffect, useState } from 'react';
import { getSupabaseClient } from '../lib/supabase';
import { getGuideTimezone } from '../utils/timezoneUtils';
import type { GuideBasicInfo } from '../types';

/**
 * Hook to fetch and manage the guide's timezone from basic_info
 *
 * Returns the guide's configured timezone (IANA ID) from their basic_info record.
 * Falls back to browser timezone if not available.
 */
export function useGuideTimezone() {
  const [timezone, setTimezone] = useState<string>(() => {
    // Initialize with browser timezone as fallback
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'America/New_York';
    }
  });
  const [basicInfo, setBasicInfo] = useState<GuideBasicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchBasicInfo = async () => {
      try {
        const supabase = getSupabaseClient();
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError || !user) {
          throw new Error('Not authenticated');
        }

        const { data, error: fetchError } = await supabase
          .from('basic_info')
          .select('timezone, utc_offset_minutes, timezone_confidence, normalized_postcode, city, state')
          .eq('user_id', user.id)
          .maybeSingle();

        if (fetchError) {
          console.warn('Failed to fetch basic_info timezone:', fetchError);
          // Don't throw - just use fallback
          return;
        }

        if (!cancelled && data) {
          setBasicInfo(data);
          const guideTimezone = getGuideTimezone(data);
          setTimezone(guideTimezone);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching guide timezone:', err);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchBasicInfo();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    timezone,
    basicInfo,
    loading,
    error,
  };
}
